import "./devtools-table.scss";
import { useEffect, useMemo, useState } from "react";
import {
  ColumnsPanelTrigger,
  DataGrid,
  ExportCsv,
  FilterPanelTrigger,
  GridActionsCellItem,
  GridApi,
  GridColDef,
  GridRowId,
  GridRowModelUpdate,
  GridRowParams,
  GridRowSelectionModel,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  QuickFilterTrigger,
  Toolbar,
  ToolbarButton,
  useGridApiContext,
  useGridSelector,
  gridRowCountSelector,
  useGridRootProps,
} from "@mui/x-data-grid";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { IconFactory } from "../icon-factory/icon-factory";
import { QueryErrorBanner, QueryErrorPanel, QueryWarningBanner } from "./query-error";
import {
  ResolvedTableModel,
  TableModel,
  resolveColumnDef,
  ShowModalFn,
} from "./table-model";
import { Duration } from "../../duration";
import { ToolbarAction } from "../../model/qubes/qubes-model";

export type QubesGridProps = {
  /** the table model that defines the data and behavior of the grid */
  tableModel: TableModel<QubesGridRow>;
  /** reference to the grid API */
  apiRef: React.MutableRefObject<GridApi | null>;
  /** function to show a modal dialog */
  showModalFn: ShowModalFn;
  /** called when a row is selected/clicked; receives the selected row id(s) */
  onRowSelection?: (
    rowIdOrRowIds: GridRowId | GridRowId[],
    columnField?: string,
  ) => void;
  /** enable multi-select checkboxes and selection model */
  multiSelect?: boolean;
  /** optional toolbar actions configured by some views */
  toolbarActions?: ToolbarAction[];
  /** custom no-rows overlay text (e.g. "Select a table to see its fields") */
  emptyMessage?: string;
};

import { selectedRowIds } from "./qubes-grid-selection";

type QubesGridRow = { [key: string]: unknown };

function ToolbarDivider() {
  return (
    <div
      aria-hidden="true"
      style={{
        alignSelf: "center",
        borderLeft: "1px solid currentColor",
        height: 24,
        opacity: 0.24,
        margin: "0 4px",
      }}
    />
  );
}

/**
 * The no-rows overlay when the query failed. A module-level component, not an
 * inline one: the grid re-renders on every retry, and an inline slot would
 * remount the panel each time and collapse its "Show query".
 */
declare module "@mui/x-data-grid" {
  interface NoRowsOverlayPropsOverrides {
    error?: string;
    query?: string;
    retryEveryMs?: number;
  }
}

function QueryErrorOverlay(props: { error?: string; query?: string; retryEveryMs?: number }) {
  if (!props.error) return null;
  return (
    <div
      style={{
        display: "flex",
        height: "100%",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <QueryErrorPanel error={props.error} query={props.query} retryEveryMs={props.retryEveryMs} />
    </div>
  );
}

/**
 * Custom toolbar component that shows refresh status
 */
function CustomToolbar({
  isRefreshing,
  lastRefreshedAt,
  error,
  errorQuery,
  degraded,
  toolbarActions,
  ..._props
}: {
  isRefreshing?: boolean;
  lastRefreshedAt?: number;
  /** the last fetch failed; shown here only while earlier rows are on screen */
  error?: string;
  errorQuery?: string;
  /** the grid runs without its optional columns */
  degraded?: { columns: string[]; labels: string[]; reason: string };
  toolbarActions?: ToolbarAction[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- MUI passes arbitrary slot props through
  [key: string]: any;
}) {
  const [currentTime, setCurrentTime] = useState(Date.now());
  const gridApiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const rowCount = useGridSelector(gridApiRef, gridRowCountSelector);

  const BaseTooltip = rootProps.slots.baseTooltip;
  const ColumnSelectorIcon = rootProps.slots.columnSelectorIcon;
  const FilterIcon = rootProps.slots.openFilterButtonIcon;
  const ExportIcon = rootProps.slots.exportIcon;
  const SearchIcon = rootProps.slots.quickFilterIcon;
  const ClearIcon = rootProps.slots.quickFilterClearIcon;
  const Badge = rootProps.slots.baseBadge;
  const IconButton = rootProps.slots.baseIconButton;

  // Update current time every second for relative timestamp
  useEffect(() => {
    const updateInterval = Duration.fromSeconds(1);
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, updateInterval.toMilliseconds());
    return () => clearInterval(timer);
  }, []);

  const getTimeAgo = (timestamp: number) => {
    const elapsed = Duration.fromMilliseconds(currentTime - timestamp);
    const seconds = Math.floor(elapsed.toSeconds());
    if (seconds < 5) return "just now";
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "1rem",
        padding: "0.5rem",
        borderBottom: "1px solid var(--DataGrid-rowBorderColor)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: "0.5rem",
          fontSize: "0.875rem",
          color: "#666",
          minWidth: "180px", // this ensures the toolbar doesn't shift too much when the refresh status appears/disappears
        }}
      >
        {isRefreshing && (
          <span
            role="status"
            aria-label="Loading table data"
            style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              aria-hidden="true"
              style={{ animation: "spin 1s linear infinite" }}
            >
              <circle
                cx="8"
                cy="8"
                r="6"
                fill="none"
                stroke="#666"
                strokeWidth="2"
                strokeDasharray="10 32"
              />
            </svg>
            Loading...
          </span>
        )}
        {error && rowCount > 0 ? (
          <QueryErrorBanner error={error} query={errorQuery} lastRefreshedAt={lastRefreshedAt} />
        ) : degraded ? (
          <QueryWarningBanner degraded={degraded} />
        ) : (
          lastRefreshedAt &&
          !isRefreshing &&
          !error && <span>Last refreshed: {getTimeAgo(lastRefreshedAt)}</span>
        )}
      </div>
      <Toolbar
        aria-label="Table actions"
        style={{ flex: 1, borderBottom: 0, padding: 0, minHeight: 0 }}
      >
        {!rootProps.disableColumnSelector && (
          <BaseTooltip
            title={gridApiRef.current.getLocaleText("toolbarColumns")}
          >
            <ColumnsPanelTrigger render={<ToolbarButton />}>
              <ColumnSelectorIcon fontSize="small" />
            </ColumnsPanelTrigger>
          </BaseTooltip>
        )}
        {!rootProps.disableColumnFilter && (
          <BaseTooltip
            title={gridApiRef.current.getLocaleText("toolbarFilters")}
          >
            <FilterPanelTrigger
              render={(triggerProps, state) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- MUI render-prop bag, spread onto ToolbarButton
                const toolbarButtonProps = triggerProps as any;
                return (
                  <ToolbarButton
                    {...toolbarButtonProps}
                    color={state.filterCount > 0 ? "primary" : "default"}
                  >
                    <Badge
                      badgeContent={state.filterCount}
                      color="primary"
                      variant="dot"
                    >
                      <FilterIcon fontSize="small" />
                    </Badge>
                  </ToolbarButton>
                );
              }}
            />
          </BaseTooltip>
        )}
        <ToolbarDivider />
        <BaseTooltip title={gridApiRef.current.getLocaleText("toolbarExport")}>
          <ExportCsv
            render={<ToolbarButton />}
            aria-label={gridApiRef.current.getLocaleText("toolbarExport")}
          >
            <ExportIcon fontSize="small" />
          </ExportCsv>
        </BaseTooltip>
        <ToolbarDivider />
        {toolbarActions && toolbarActions.length > 0 && (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                marginRight: "0.25rem",
              }}
            >
              {toolbarActions.map((action) => (
                <Button
                  key={action.name}
                  onClick={() => action.run()}
                  variant="outlined"
                  size="small"
                  startIcon={IconFactory.getIcon(action.icon)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderColor: "#1e3a5f",
                    color: "#e0e0e0",
                    backgroundColor: "rgba(13, 34, 64, 0.35)",
                    "&:hover": {
                      borderColor: "#2c4d79",
                      backgroundColor: "rgba(13, 34, 64, 0.55)",
                    },
                  }}
                >
                  {action.name}
                </Button>
              ))}
            </div>
            <ToolbarDivider />
          </>
        )}
        <QuickFilter
          render={(quickFilterProps) => (
            <div
              {...quickFilterProps}
              style={{
                ...quickFilterProps.style,
                display: "grid",
                alignItems: "center",
              }}
            />
          )}
        >
          <QuickFilterTrigger
            render={(triggerProps, state) => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any -- MUI render-prop bag, spread onto ToolbarButton
              const toolbarButtonProps = triggerProps as any;
              return (
                <BaseTooltip
                  title={gridApiRef.current.getLocaleText(
                    "toolbarQuickFilterLabel",
                  )}
                >
                  <ToolbarButton
                    {...toolbarButtonProps}
                    aria-disabled={state.expanded}
                    style={{
                      ...triggerProps.style,
                      gridArea: "1 / 1",
                      opacity: state.expanded ? 0 : 1,
                      pointerEvents: state.expanded ? "none" : "auto",
                    }}
                  >
                    <SearchIcon fontSize="small" />
                  </ToolbarButton>
                </BaseTooltip>
              );
            }}
          />
          <QuickFilterControl
            render={({ ref, slotProps, ...controlProps }, state) => (
              <TextField
                {...controlProps}
                inputRef={ref}
                placeholder={gridApiRef.current.getLocaleText(
                  "toolbarQuickFilterPlaceholder",
                )}
                size="small"
                slotProps={slotProps}
                style={{
                  ...controlProps.style,
                  gridArea: "1 / 1",
                  width: state.expanded ? 260 : 0,
                  opacity: state.expanded ? 1 : 0,
                  overflow: "hidden",
                }}
              />
            )}
          />
          <QuickFilterClear
            render={(clearProps, state) => {
              const showClear = state.expanded && state.value;
              // eslint-disable-next-line @typescript-eslint/no-explicit-any -- MUI render-prop bag
              const clearButtonProps = clearProps as any;

              return (
                <IconButton
                  {...clearButtonProps}
                  size="small"
                  edge="end"
                  disabled={!showClear}
                  aria-label={gridApiRef.current.getLocaleText(
                    "toolbarQuickFilterDeleteIconLabel",
                  )}
                  style={{
                    ...clearProps.style,
                    gridArea: "1 / 1",
                    justifySelf: "end",
                    opacity: showClear ? 1 : 0,
                    pointerEvents: showClear ? "auto" : "none",
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              );
            }}
          />
        </QuickFilter>
      </Toolbar>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

/**
 * A data grid component using MUI DataGrid to display tabular data.
 */
export const QubesGrid: React.FC<QubesGridProps> = ({
  tableModel,
  apiRef,
  showModalFn,
  onRowSelection,
  multiSelect,
  toolbarActions,
  emptyMessage,
}) => {
  const resolvedTableModel = useMemo(
    () => new ResolvedTableModel(tableModel),
    [tableModel],
  );

  const [rows, setRows] = useState<QubesGridRow[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<number | undefined>();
  const [degraded, setDegraded] = useState<{ columns: string[]; labels: string[]; reason: string } | undefined>();
  const [fetchError, setFetchError] = useState<
    { error: string; query?: string; retryEveryMs?: number } | undefined
  >();
  // Single-select highlight: the row the user last clicked (e.g. the selected
  // schedule that drives the dependent Events grid). Drives MUI's rowSelectionModel
  // so the selected row is clearly highlighted. Not used in multiSelect mode (that
  // uses checkbox selection).
  const [selectedRowId, setSelectedRowId] = useState<GridRowId | undefined>();

  /**
   * Subscribe to realtime updates from the table model and update rows accordingly
   */
  useEffect(() => {
    const _realtimeSubscription = tableModel.realtime?.subscribe({
      next: (change) => {
        const rowUpdates = new Array<GridRowModelUpdate>();
        if (change.type === "upsert") {
          const rowId = tableModel.rowId(change.row);
          tableModel.currentRows.set(rowId, change.row);
          rowUpdates.push({ id: rowId, row: change.row });
        } else if (change.type === "delete") {
          rowUpdates.push({ id: change.rowId, _action: "delete" });
        }
        rowUpdates.forEach((rowUpdate) =>
          apiRef.current?.updateRows([rowUpdate]),
        );
      },
    });

    setRows(
      tableModel.initialRows.map((dataRow, _index) => {
        const rowId = tableModel.rowId(dataRow);
        tableModel.currentRows.set(rowId, dataRow);
        return { id: rowId, row: dataRow };
      }),
    );

    return () => {
      _realtimeSubscription?.unsubscribe();
    };
  }, [apiRef, tableModel]);

  /**
   * Subscribe to refresh state updates
   */
  useEffect(() => {
    const refreshSubscription = tableModel.refreshState?.subscribe({
      next: (state) => {
        setIsRefreshing(state.isRefreshing);
        setLastRefreshedAt(state.lastRefreshedAt);
        setDegraded(state.degraded);
        setFetchError(
          state.error
            ? { error: state.error, query: state.errorQuery, retryEveryMs: state.retryEveryMs }
            : undefined,
        );
      },
    });

    return () => {
      refreshSubscription?.unsubscribe();
    };
  }, [tableModel]);

  const columns: GridColDef<QubesGridRow>[] = useMemo(() => {
    const columnDefs = tableModel.columns.map((col) =>
      resolveColumnDef(tableModel, col),
    );
    // Only add actions column if there are actions defined
    if (tableModel.actions && tableModel.actions.length > 0) {
      const actionColumn: GridColDef<QubesGridRow> = {
        field: "actions",
        type: "actions",
        getActions: (params: GridRowParams) =>
          tableModel.actions?.map((action) => (
            <GridActionsCellItem
              key={action.name}
              showInMenu={true}
              label={action.name}
              icon={IconFactory.getIcon(action.icon ?? "Default")} //TODO grab this from the action
              onClick={() => {
                // get the latest row data from the table model in case it has changed since the row was rendered
                const api = apiRef.current;
                if (!api) return; // should never happen, but just in case
                const row = tableModel.currentRows.get(
                  params.row.id,
                ) as QubesGridRow;
                if (action.apply) {
                  action.apply(tableModel, api, row);
                }
                if (action.run) {
                  action.run({
                    tableModel,
                    api,
                    row,
                    showModalFn,
                  });
                }
              }}
            />
          )) ?? [],
      };
      columnDefs.push(actionColumn);
    }
    return columnDefs;
  }, [apiRef, showModalFn, tableModel]);

  const toolbarProps = {
    isRefreshing,
    lastRefreshedAt,
    error: fetchError?.error,
    errorQuery: fetchError?.query,
    degraded,
    toolbarActions,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- MUI GridToolbarProps slot typing
  } as any;

  return (
    <DataGrid
      sx={{
        "--DataGrid-overlayHeight": "160px",
        "--DataGrid-t-header-background-base": "#0d2240",
        backgroundColor: "#0d1b2e",
        color: "#e0e0e0",
        "& .MuiDataGrid-overlayWrapper, & .MuiDataGrid-overlayWrapperInner, & .MuiDataGrid-overlay":
          {
            minHeight: "160px",
          },
        "& .MuiDataGrid-main, & .MuiDataGrid-virtualScroller, & .MuiDataGrid-columnHeaders, & .MuiDataGrid-footerContainer":
          {
            backgroundColor: "#0d1b2e",
          },
        "& .MuiDataGrid-columnHeaders": {
          color: "#e0e0e0",
          borderBottomColor: "#1e3a5f",
        },
        "& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell, & .MuiDataGrid-row":
          {
            backgroundColor: "#0d1b2e",
            color: "#e0e0e0",
          },
        "& .MuiDataGrid-cell": {
          borderBottomColor: "#1e3a5f",
        },
        // Clearly highlight the selected row (e.g. the chosen schedule).
        "& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover":
          {
            backgroundColor: "#1e4976",
            "& .MuiDataGrid-cell": { backgroundColor: "#1e4976" },
          },
      }}
      initialState={{
        columns: {
          columnVisibilityModel: resolvedTableModel.columnVisibilityModel,
        },
      }}
      apiRef={apiRef}
      rows={rows}
      getRowId={(tableRow) => tableRow.id as GridRowId}
      columns={columns}
      // A failing grid keeps its error panel up through each retry instead of
      // flashing the loading overlay over it.
      loading={isRefreshing && !fetchError}
      showToolbar
      slots={{
        toolbar: CustomToolbar,
        // Custom empty state: say WHY the grid is empty (e.g. "Select a table
        // to see its fields") instead of the bare default "No rows". A failed
        // query is never shown as empty: it gets the error panel.
        ...(fetchError
          ? { noRowsOverlay: QueryErrorOverlay }
          : emptyMessage
          ? {
              noRowsOverlay: () => (
                <div
                  style={{
                    display: "flex",
                    height: "100%",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: 0.6,
                    fontStyle: "italic",
                    padding: 16,
                    textAlign: "center",
                  }}
                >
                  {emptyMessage}
                </div>
              ),
            }
          : {}),
      }}
      slotProps={{
        toolbar: toolbarProps,
        ...(fetchError ? { noRowsOverlay: fetchError } : {}),
      }}
      checkboxSelection={!!multiSelect} // enable checkboxes if multiSelect is true
      // Single-select highlight: reflect the clicked row in MUI's selection model
      // so .Mui-selected styling applies. multiSelect uses checkbox selection and
      // manages its own model, so leave it alone there.
      rowSelectionModel={
        !multiSelect && selectedRowId !== undefined
          ? { type: "include", ids: new Set([selectedRowId]) }
          : undefined
      }
      onCellClick={(params, _event, _details) => {
        if (multiSelect) return; // use selection model for multiselect
        try {
          const rowId = params.row.id;
          const columnField = params.field;
          // only trigger selection if not clicking on an action cell
          const columnDef = columns.find((col) => col.field === columnField);
          if (columnDef?.type === "actions") return;
          setSelectedRowId(rowId); // highlight the clicked row
          if (onRowSelection) onRowSelection(rowId, columnField);
        } catch (_e) {
          // swallow
        }
      }}
      // onRowClick={(params, event, details) => {
      //   if (multiSelect) return; // use selection model for multiselect
      //   try {
      //     const dataRow = params.row?.row ?? params.row;
      //     if (onRowSelection) onRowSelection(dataRow?.row ?? dataRow);
      //   } catch (e) {
      //     // swallow
      //   }
      // }}
      onRowSelectionModelChange={(selectionModel: GridRowSelectionModel) => {
        if (!multiSelect) return; // only handle if multiSelect is enabled
        try {
          // Emit row IDS, not row objects — see selectedRowIds above.
          const ids = selectedRowIds(
            selectionModel,
            tableModel.currentRows.keys(),
          );
          if (onRowSelection) onRowSelection(ids);
        } catch (_e) {
          // swallow
        }
      }}
    />
  );
};
